import { useEffect, useRef, useState } from 'react';
import { m, AnimatePresence } from 'motion/react';
import { ArrowDown, Play, X } from 'lucide-react';
import { trackCtaClick } from '../../lib/analytics';
import { Picture } from './Picture';

const IMAGE_SLIDES = [
  { media: 'interior/living-1',      alt: 'Lüks salon — kristal avizeli oturma alanı' },
  { media: 'interior/bedroom-1',     alt: 'Ebeveyn yatak odası — özel giyinme odası' },
  { media: 'interior/kitchen-1',     alt: 'Modern mutfak — manzaralı yemek alanı' },
  { media: 'interior/living-2',      alt: 'Salon — şömine ve TV duvarı' },
  { media: 'interior/hero-rainnova', alt: 'Rain Nova rezidans dış cephe' },
];

const SLIDE_DURATION = 5000;

const SPARKLES = [
  { top: '-26px', right: '-26px', size: 22 },
  { top: '-10px', right: '-44px', size: 13 },
  { top: '-40px', right: '-10px', size: 10 },
];

const STAR_PATH = 'M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z';

const GRAIN_SVG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")";

const sparkleVariants = {
  hidden: { opacity: 0, scale: 0, rotate: 0 },
  visible: (i: number) => ({
    opacity: [0, 1, 0.8, 0],
    scale: [0, 1.2, 0.9, 0],
    rotate: [0, 30, -20, 0],
    transition: {
      duration: 1.4,
      delay: 1.6 + i * 0.2,
      repeat: Infinity,
      repeatDelay: 3,
      ease: 'easeInOut' as const,
    },
  }),
};

// Hero metni LCP elemanı. Görünürlüğü animasyona bağlamıyoruz — ilk boyamada
// tam opaklıkta gelir, sadece transform animasyonu yapar (composited, layout tetiklemez).
const riseIn = {
  initial: { y: 24 },
  animate: { y: 0 },
};

const HERO_TEXT_SHADOW = '0 2px 20px rgba(180,200,220,0.2)';
const HERO_WORD_STYLE = {
  fontSize: 'clamp(3rem, 8vw, 6rem)',
  fontWeight: 400,
  color: 'rgba(255,255,255,0.92)',
  textShadow: HERO_TEXT_SHADOW,
} as const;

export function Hero() {
  const [index, setIndex] = useState(0);
  const [videoOn, setVideoOn] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Görsel slaytları döndür — video açıkken dur
  useEffect(() => {
    if (videoOn) return;
    const t = setTimeout(() => setIndex((i) => (i + 1) % IMAGE_SLIDES.length), SLIDE_DURATION);
    return () => clearTimeout(t);
  }, [index, videoOn]);

  // Video yalnızca kullanıcı isteğiyle iner; açıldığında oynat
  useEffect(() => {
    if (videoOn) videoRef.current?.play().catch(() => {});
  }, [videoOn]);

  const openVideo = () => {
    trackCtaClick('Tanıtım Filmi', 'hero');
    setVideoOn(true);
  };

  return (
    <section
      id="hero"
      className="relative flex items-center justify-center overflow-hidden h-[100svh]"
    >
      {/* Arka plan */}
      <div className="absolute inset-0">
        {videoOn ? (
          <video
            ref={videoRef}
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            onEnded={() => setVideoOn(false)}
            className="absolute inset-0 w-full h-full object-cover scale-[1.22] origin-center"
            poster="/img/hero/poster-1280.webp"
          >
            <source src="/video/hero-720.webm" type="video/webm" />
            <source src="/video/hero-720.mp4" type="video/mp4" />
          </video>
        ) : (
          <AnimatePresence mode="sync">
            <m.div
              key={`slide-${index}`}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.03 }}
              transition={{
                opacity: { duration: 1.2, ease: 'easeInOut' },
                scale: { duration: SLIDE_DURATION / 1000 + 1.2, ease: 'linear' },
              }}
              className="absolute inset-0"
            >
              <Picture
                media={IMAGE_SLIDES[index].media}
                alt={IMAGE_SLIDES[index].alt}
                sizes="100vw"
                width={1920}
                className="absolute inset-0 w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
              />
            </m.div>
          </AnimatePresence>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-900/50 to-slate-950/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(2,6,23,0.75)_100%)]" />
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: GRAIN_SVG }}
        />
      </div>

      {/* Video kapatma */}
      {videoOn && (
        <button
          type="button"
          onClick={() => setVideoOn(false)}
          aria-label="Tanıtım filmini kapat"
          className="absolute top-24 right-4 z-20 flex items-center justify-center w-11 h-11 rounded-full bg-black/40 text-white backdrop-blur-sm border border-white/20 hover:bg-black/60 transition-colors"
        >
          <X size={20} aria-hidden="true" />
        </button>
      )}

      {/* Badge */}
      <m.div
        initial={{ y: -8 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="absolute top-[12%] left-1/2 -translate-x-1/2 z-10"
      >
        <span className="inline-block px-3 py-1.5 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full text-white/90 text-[10px] sm:text-sm tracking-[0.2em] whitespace-nowrap">
          LÜKS YAŞAM PROJESİ
        </span>
      </m.div>

      {/* Orta içerik */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 md:mb-6 relative inline-block">
            <h1 className="leading-none mb-2 relative" style={{ fontFamily: "'Great Vibes', cursive" }}>
              <m.span
                {...riseIn}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block mr-3"
                style={HERO_WORD_STYLE}
              >
                Rain
              </m.span>
              <m.span
                {...riseIn}
                transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block relative"
                style={HERO_WORD_STYLE}
              >
                Nova
                {SPARKLES.map((pos, i) => (
                  <m.svg
                    key={i}
                    custom={i}
                    variants={sparkleVariants}
                    initial="hidden"
                    animate="visible"
                    width={pos.size}
                    height={pos.size}
                    viewBox="0 0 24 24"
                    fill="white"
                    aria-hidden="true"
                    className="absolute pointer-events-none"
                    style={{ top: pos.top, right: pos.right }}
                  >
                    <path d={STAR_PATH} />
                  </m.svg>
                ))}
              </m.span>
            </h1>

            <m.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              style={{ transformOrigin: 'center' }}
              className="h-px w-40 mx-auto mb-2 md:mb-3"
            >
              <div
                className="h-px w-full"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)' }}
              />
            </m.div>

            <span className="block text-[9px] sm:text-sm font-light tracking-[0.3em] uppercase text-white/50">
              İstanbul'un Yeni Yaşam Yıldızı
            </span>
          </div>

          {/* LCP elemanı — animasyonsuz, ilk boyamada görünür */}
          <p className="text-sm md:text-xl text-gray-300 mb-8 md:mb-12 max-w-xs md:max-w-2xl mx-auto tracking-wide leading-relaxed">
            Modern mimari, prestijli yaşam alanları ve eşsiz sosyal donatılarla
            hayalinizdeki evi Baran Group farkı ile keşfedin.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
            <a
              href="#contact"
              onClick={() => trackCtaClick('Kataloğu İndir', 'hero')}
              className="w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] transform text-center font-medium text-sm md:text-base"
            >
              Kataloğu İndir
            </a>
            <a
              href="#about"
              onClick={() => trackCtaClick('Projeyi İncele', 'hero')}
              className="w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-lg hover:bg-white/20 transition-all duration-300 text-center text-sm md:text-base"
            >
              Projeyi İncele
            </a>
          </div>

          {!videoOn && (
            <button
              type="button"
              onClick={openVideo}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white text-xs md:text-sm tracking-wide transition-colors"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm">
                <Play size={13} fill="currentColor" aria-hidden="true" />
              </span>
              Tanıtım filmini izle
            </button>
          )}
        </div>
      </div>

      {/* Slayt göstergeleri — görsel 6px, dokunma hedefi 24px */}
      {!videoOn && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center">
          {IMAGE_SLIDES.map((slide, i) => (
            <button
              key={slide.media}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`${i + 1}. görsele geç: ${slide.alt}`}
              aria-current={i === index}
              // Dokunma hedefi her durumda >=24px; aktif çubuk 24px olduğu için
              // kutusu 32px'e çıkar, yoksa komşularına yapışır.
              className={`flex items-center justify-center h-6 ${i === index ? 'w-8' : 'w-6'}`}
            >
              <span
                aria-hidden="true"
                className={`block rounded-full transition-all duration-500 ${
                  i === index ? 'w-6 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'
                }`}
              />
            </button>
          ))}
        </div>
      )}

      {/* Scroll göstergesi — masaüstünde */}
      <div className="hidden md:block absolute bottom-8 right-8 z-10">
        <m.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="text-white/60"
        >
          <ArrowDown size={28} aria-hidden="true" />
        </m.div>
      </div>
    </section>
  );
}
